import { Component, OnDestroy, OnInit } from '@angular/core';
import { IBaseWidgetConfig } from '../widget/base/widget-list';
import { BaseWidgetComponent } from '../widget/base/base-widget';
import { RemoteDataWrapperService } from 'src/app/shared/services/remote-data/remote-data-wrapper.service';
import { WidgetDataService } from '../widget/data/widget-data.service';
import { Observable, Subscription, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { DxButtonModule } from 'devextreme-angular';
import { ToastrService } from 'ngx-toastr';
import { DashboardDataService } from '../../data/dashboard-data.service';
import { AuthHelperService } from 'src/app/core/auth/auth-helper.service';
import { MachineInfoMainService } from '../../../../shared/services/machine-info.service';

/**
 * Correctors widget config
 *
 * @export
 * @interface ICorrectorsWidgetConfgi
 * @extends {IBaseWidgetConfig}
 */
export interface ICorrectorsWidgetConfig extends IBaseWidgetConfig {
  // Consist only IBasicWidgetConfig properties. But can be extended in future.
  // and maintains consistency naming in all widgets (project).
}

interface Corrector {
  Name: string;
  Label: string;
  ApplyNTimes: number;
  GroupEnable?: boolean;
}

enum CorrectorOperations {
  INCREMENT = 'Inc',
  DECREMENT = 'Dec',
  TOGGLE = 'Toggle'
}

@Component({
  selector: 'app-correctors-widget',
  standalone: true,
  imports: [CommonModule, DxButtonModule],
  templateUrl: './correctors-widget.component.html',
  styleUrl: './correctors-widget.component.scss'
})
export class CorrectorsWidgetComponent extends BaseWidgetComponent<ICorrectorsWidgetConfig> implements OnDestroy, OnInit {
  
  protected correctors: Corrector[] = [];
  private _applyRemoteChangesFuncId?: number;
  private _applyRemoteChangesRole?: string;

  constructor(
    widgetDataService: WidgetDataService<ICorrectorsWidgetConfig>, 
    remoteDataWrapperService: RemoteDataWrapperService,
    private readonly _toaster: ToastrService,
    private readonly _dashboardDataService: DashboardDataService,
    private readonly _authHelperSvc: AuthHelperService,
  ) {
    super(widgetDataService, remoteDataWrapperService);
  }

  protected override setOptions(options: ICorrectorsWidgetConfig | undefined): void {
    if (options) {
      if (options.machineName && options.machineName !== this.options?.machineName) {
        this._initialize(options);
      }

      this.options = {...options};
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this._dashboardDataService.widgetInitialized();  
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this._dashboardDataService.widgetDestroyed();
  }

  private _structureSub?: Subscription;
  private _correctorsSub?: Subscription;
  private _rawStructureSub?: Subscription;

  private _initialize(options: ICorrectorsWidgetConfig): void {
    if (options.machineName) {
      this.remoteDataWrapperService.watchRemoteDataUpdates(options.machineName, true);
      this._structureSub?.unsubscribe();
      this._structureSub = this.remoteDataWrapperService.getMachineStructure(options.machineName).pipe(takeUntil(this.unSubscriber)).subscribe(val => {
        const observable = val?.MachineOne?.CorrectionCtx?.CorrectorsState;

        if (observable && observable instanceof Observable) {
          this._correctorsSub?.unsubscribe();
          
          this._correctorsSub = observable.subscribe((correctorsString: string) => {
            try {
              this.correctors = JSON.parse(correctorsString) as Corrector[];
            } catch (e) {
              console.error(e)
            }
          });
        }
      });

      this._rawStructureSub?.unsubscribe();
      this._rawStructureSub = this.remoteDataWrapperService.getMachineStructureRaw(options.machineName).pipe(takeUntil(this.unSubscriber)).subscribe(val => {
        if (val && val !== '{}') {
          try {
            const parsed = JSON.parse(val);
            this._applyRemoteChangesFuncId = parsed.MachineOne.CorrectionCtx.ApplyRemoteChanges._ID;
            this._applyRemoteChangesRole = parsed.MachineOne.CorrectionCtx.ApplyRemoteChanges.Role;
          } catch (e) {
            console.error(e);
          }
        }
      });
    }
  }

  protected increment(item: Corrector): void {
    this._invokeOperation(item, CorrectorOperations.INCREMENT);
  }

  protected decrement(item: Corrector): void {
    this._invokeOperation(item, CorrectorOperations.DECREMENT);
  }

  protected toggle(item: Corrector): void {
    this._invokeOperation(item, CorrectorOperations.TOGGLE);
  }

  private async _invokeOperation(item: Corrector, operation: CorrectorOperations): Promise<void> {
    if (this.options?.machineName && this._applyRemoteChangesFuncId) {
      let result = await this.remoteDataWrapperService.invokeOnSupplierById(this.options.machineName, this._applyRemoteChangesFuncId, [item.Name, operation]);

      if (result) {
        this._toaster.success('Operation done.');
      } else {
        this._toaster.error('Operation failed.');
      }
    }
  }

  protected isEnabledByFenixRole(): Observable<boolean> {
    return this._authHelperSvc.isFenixActionEnabled(this._applyRemoteChangesRole ?? '');
  }
}
